import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  PlayCircle, MessageSquareText, Lightbulb, Target, RotateCcw, ArrowRight, ArrowDown,
  ListChecks, Users, Scale, ShieldAlert, Waves, Mountain,
} from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { CATEGORIES } from "@/data/simulations";

const ACCENT = "#4FA3D9";

const CATEGORY_ICON = {
  "everyday-life-skills": ListChecks,
  "social-relationships": Users,
  "decisions-pressure": Scale,
  "safety-emergencies": ShieldAlert,
  "change-uncertainty": Waves,
  "setbacks-adversity": Mountain,
};

const rise = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const Reveal = ({ children, className, delay = 0 }) => (
  <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} transition={{ delay }} className={className}>
    {children}
  </motion.div>
);

const TM = ({ nudge = "-0.08em" }) => <sup className="align-super text-[0.55em]" style={{ marginLeft: nudge }}>™</sup>;

export const Landing = () => {
  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="landing-page">
      <Seo
        title="Adaptience™ | Prepare for Life's Unpredictable Moments"
        description="Practise navigating difficult, uncertain and unexpected situations through interactive life simulations designed to help you prepare before facing them for real."
        canonical="/"
      />
      {/* subtle top glow */}
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[60vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>

      <Navbar />

      <main className="relative z-10 mx-auto w-full max-w-4xl px-6 sm:px-10">

        {/* SECTION 1 — HERO */}
        <section className="flex min-h-[80vh] flex-col justify-center py-20" data-testid="hero-section">
          <Reveal>
            <h1 className="max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-6xl">
              Prepare for the parts of life you can't predict.
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70 sm:text-xl">
              Practise difficult real-life situations through interactive simulations — make decisions, see how you respond, and discover what you could strengthen.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/simulations" data-testid="hero-explore-btn" className="group mt-10 inline-flex w-fit items-center gap-3 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]" style={{ background: ACCENT }}>
              Explore Simulations
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </section>

        <Divider />

        {/* SECTION 2 — WHY ADAPTIENCE */}
        <section className="py-24 sm:py-28" data-testid="why-section">
          <Reveal>
            <p className="font-display text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              Life doesn't always go as planned.
            </p>
            <p className="mt-2 font-display text-3xl font-medium leading-tight tracking-tight text-white/50 sm:text-4xl">
              Yet we rarely practise for when it doesn't.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-14 max-w-2xl text-lg leading-relaxed text-white/70 sm:text-xl">
              We prepare for the parts of life we can see coming. But the moments that shape us most are often the ones we face for the first time when they actually happen.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/60">
              Adaptience<TM /> is the learned human capability to prepare for, navigate, adapt to, recover from, and grow through life's inevitable uncertainty.
            </p>
          </Reveal>
        </section>

        <Divider />

        {/* SECTION 3 — WHAT CAN YOU PRACTISE? */}
        <section className="py-24 sm:py-28" data-testid="categories-section">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              What can you practise?
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/60">
              Adaptience<TM /> spans the situations life brings — across six areas of real experience.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-cards">
              {CATEGORIES.map((c) => {
                const Icon = CATEGORY_ICON[c.slug] || ListChecks;
                return (
                  <Link
                    key={c.slug}
                    to={`/simulations/${c.slug}`}
                    data-testid={`home-category-${c.slug}`}
                    className="group flex flex-col rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 text-left transition-colors hover:border-white/25"
                  >
                    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/12" style={{ color: ACCENT }}>
                      <Icon size={20} strokeWidth={1.3} />
                    </div>
                    <h3 className="font-display text-lg font-medium tracking-tight text-white">{c.name}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-white/60">{c.description}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: ACCENT }}>
                      Explore
                      <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <Link
              to="/simulations"
              data-testid="explore-all-btn"
              className="group mt-12 inline-flex items-center gap-3 rounded-full border px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] transition-[color,background-color] duration-500 active:scale-[0.98]"
              style={{ borderColor: ACCENT, color: ACCENT }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = ACCENT; }}
            >
              Explore All Simulations
              <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </section>

        <Divider />

        {/* SECTION 4 — HOW ADAPTIENCE WORKS */}
        <section className="py-24 sm:py-28" data-testid="how-section">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              Step into the situation.
              <span className="block" style={{ color: ACCENT }}>See what you would do.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/60">
              Experience a realistic scenario, make your own decisions, and see how your response unfolds.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-2" data-testid="how-steps">
              <Step icon={<PlayCircle size={22} strokeWidth={1.3} />} title="Experience" text="Enter the situation." />
              <StepArrow />
              <Step icon={<MessageSquareText size={22} strokeWidth={1.3} />} title="Respond" text="Make your own decisions." />
              <StepArrow />
              <Step icon={<Lightbulb size={22} strokeWidth={1.3} />} title="Reflect" text="See how you responded." />
              <StepArrow />
              <Step icon={<Target size={22} strokeWidth={1.3} />} title="Understand" text="Discover what you could strengthen." />
              <StepArrow />
              <Step icon={<RotateCcw size={22} strokeWidth={1.3} />} title="Practise again" text="Try another way." />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-20 border-l-2 pl-6" style={{ borderColor: ACCENT }}>
              <p className="font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl">
                Advice tells us what we should do.
              </p>
              <p className="mt-1 font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl" style={{ color: ACCENT }}>
                Practice reveals what we actually would do.
              </p>
            </div>
          </Reveal>
        </section>

        <Divider />

        {/* SECTION — VALUE / OUTCOME + PRACTICE REPORT + FRAMEWORK */}
        <section className="py-24 sm:py-28" data-testid="value-section">
          <Reveal>
            <h2 className="max-w-3xl font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              Practise for what life doesn't teach in advance.
            </h2>
            <p className="mt-3 max-w-2xl font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl" style={{ color: ACCENT }}>
              Discover how you respond before it matters.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8" data-testid="outcomes">
              <Outcome title="Practise" text="Experience the situation." />
              <Outcome title="Understand" text="See how you respond." />
              <Outcome title="Strengthen" text="Know what to practise next." />
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <p className="mt-14 max-w-2xl text-sm leading-relaxed text-white/65" data-testid="practice-report-line">
              Complete a simulation and receive your personal Adaptience<TM /> Practice Report.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-20 flex flex-wrap items-center gap-x-4 gap-y-3 sm:gap-x-5" data-testid="framework">
              {["Prepare", "Navigate", "Adapt", "Recover", "Grow"].map((w, i) => (
                <span key={w} className="flex items-center gap-x-4 sm:gap-x-5">
                  <span
                    className="font-display text-base font-semibold uppercase tracking-[0.14em] sm:text-lg"
                  >
                    {w}
                  </span>
                  {i < 4 ? <ArrowRight size={15} style={{ color: ACCENT }} className="opacity-70" /> : null}
                </span>
              ))}
            </div>
          </Reveal>
        </section>

        <Divider />

        {/* SECTION 5 — BROADER PRACTICE IDEA */}
        <section className="py-24 sm:py-28" data-testid="broader-section">
          <Reveal>
            <p className="max-w-3xl font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl">
              Uncertainty, decisions, relationships, safety, change and setbacks aren't rare exceptions. They're part of every life.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/60">
              Practice gives us a mental reference point — allowing us to rehearse how we might respond before facing a situation when the stakes are real.
            </p>
          </Reveal>
        </section>

        <Divider />

        {/* SECTION 6 — FINAL CTA */}
        <section className="py-24 text-center sm:py-28" data-testid="final-cta">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              We can't predict every situation life will bring.
            </h2>
            <p className="mx-auto mt-2 max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl" style={{ color: ACCENT }}>
              But we can practise how we navigate it.
            </p>
            <Link
              to="/simulations"
              data-testid="final-explore-btn"
              className="group mt-12 inline-flex items-center gap-3 rounded-full px-9 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
              style={{ background: ACCENT }}
            >
              Explore Simulations
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </section>

        {/* FOOTER */}
        <SiteFooter />
      </main>
    </div>
  );
};

const Divider = () => <div className="h-px w-full bg-white/[0.07]" />;

const Step = ({ icon, title, text }) => (
  <div className="flex-1" data-testid={`step-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/12" style={{ color: ACCENT }}>
      {icon}
    </div>
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/90">{title}</p>
    <p className="mt-1.5 text-sm leading-relaxed text-white/50">{text}</p>
  </div>
);

const StepArrow = () => (
  <div className="flex items-center justify-center pt-3 text-white/25 sm:pt-5">
    <ArrowRight size={18} className="hidden sm:block" />
    <ArrowDown size={16} className="sm:hidden" />
  </div>
);

const Outcome = ({ title, text }) => (
  <div className="border-t border-white/10 pt-5" data-testid={`outcome-${title.toLowerCase()}`}>
    <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: ACCENT }}>{title}</p>
    <p className="mt-2 text-lg leading-relaxed text-white/70">{text}</p>
  </div>
);

export default Landing;
